import React, { useState, useEffect, useMemo } from "react";
import api from "../../api/api";
import { toast } from "react-toastify";
import { 
  Edit, 
  Trash, 
  X, 
  Save, 
  PlusCircle, 
  LayoutGrid, 
  Tag, 
  DollarSign, 
  Image, 
  FileText,
  UtensilsCrossed,
  Search,
  Filter
} from "lucide-react";
import * as S from "./styles";
import DeleteModal from "../DeleteModal";

const ProductAdmin = () => {
  const [products, setProducts] = useState([]);
  const [editingProduct, setEditingProduct] = useState(null);
  const [modalOpen, setModalOpen] = useState(false);
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [filterCategory, setFilterCategory] = useState("");

  const [form, setForm] = useState({
    name: "",
    description: "",
    price: "",
    category: "",
    image_url: ""
  });

  const [categories, setCategories] = useState(["Hambúrgueres", "Combo", "Acompanhamentos", "Bebidas", "Sobremesas"]);

  useEffect(() => {
    api
      .get("/categories")
      .then(({ data }) => data.length > 0 && setCategories(data.map((c) => c.name)))
      .catch(() => {});
  }, []);

  const loadProducts = async () => {
    try {
      const res = await api.get("/products");
      setProducts(res.data);
    } catch (err) {
      toast.error("Erro ao carregar produtos.");
    }
  };

  useEffect(() => {
    loadProducts();
  }, []);

  const filteredProducts = useMemo(() => {
    return products.filter((product) => {
      const matchesSearch = product.name.toLowerCase().includes(searchTerm.toLowerCase());
      const matchesCategory = filterCategory === "" || product.category === filterCategory;
      return matchesSearch && matchesCategory;
    });
  }, [products, searchTerm, filterCategory]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm(prev => ({ ...prev, [name]: value }));
  };

  const resetForm = () => {
    setForm({ name: "", description: "", price: "", category: "", image_url: "" });
    setEditingProduct(null);
  };

  const handleSave = async (e) => {
    e.preventDefault();
    if (!form.name || !form.price || !form.category) {
      return toast.warn("Preencha os campos obrigatórios!");
    }

    try {
      setIsSubmitting(true);
      if (editingProduct) {
        await api.put(`/products/${editingProduct.id}`, form);
        toast.success("Produto atualizado!");
      } else {
        await api.post("/products", form);
        toast.success("Produto cadastrado!");
      }
      resetForm();
      loadProducts();
    } catch (err) {
      toast.error(err.response?.data?.error || "Erro ao salvar o produto.");
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleEdit = (product) => {
    setEditingProduct(product);
    setForm({
      name: product.name,
      description: product.description || "",
      price: product.price,
      category: product.category,
      image_url: product.image_url || ""
    });
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const handleOpenModal = (product) => {
    setSelectedProduct(product);
    setModalOpen(true);
  };

  const handleConfirmDelete = async () => {
    try {
      await api.delete(`/products/${selectedProduct.id}`);
      toast.success("Removido com sucesso!");
      loadProducts();
    } catch (err) {
      toast.error("Erro ao excluir.");
    } finally {
      setModalOpen(false);
    }
  };

  return (
    <S.AdminContainer>
      <header>
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
          <UtensilsCrossed size={32} color="var(--primary)" />
          <h2>Gestão de Cardápio</h2>
        </div>
        <p>Controle seus produtos e preços de forma rápida.</p>
      </header>

      <S.FormCard>
        <h3>
          {editingProduct ? (
            <><Edit size={20} /> Editando Produto</>
          ) : (
            <><PlusCircle size={20} /> Novo Item no Cardápio</>
          )}
        </h3>
        
        <form onSubmit={handleSave}>
          <div className="input-group">
            <label><Tag size={14} style={{ marginRight: 4 }} /> Nome do Produto</label>
            <input
              name="name"
              value={form.name}
              onChange={handleChange}
              placeholder="Ex: Ninja Burger Double"
            />
          </div>

          <div className="input-group">
            <label><LayoutGrid size={14} style={{ marginRight: 4 }} /> Categoria</label>
            <select name="category" value={form.category} onChange={handleChange}>
              <option value="">Selecione...</option>
              {categories.map(cat => (
                <option key={cat} value={cat}>{cat}</option>
              ))}
            </select>
          </div>

          <div className="input-group">
            <label><DollarSign size={14} style={{ marginRight: 4 }} /> Preço (R$)</label>
            <input
              name="price"
              type="number"
              step="0.01"
              value={form.price}
              onChange={handleChange}
              placeholder="0,00"
            />
          </div>

          <div className="input-group">
            <label><Image size={14} style={{ marginRight: 4 }} /> URL da Imagem</label>
            <input
              name="image_url"
              value={form.image_url}
              onChange={handleChange}
              placeholder="https://link.com/foto.jpg"
            />
          </div>

          <div className="input-group full-width">
            <label><FileText size={14} style={{ marginRight: 4 }} /> Descrição</label>
            <input
              name="description"
              value={form.description}
              onChange={handleChange}
              placeholder="Pão, carne, queijo..."
            />
          </div>

          <div className="actions">
            <button type="submit" className="save-btn" disabled={isSubmitting}>
              {isSubmitting ? "Processando..." : (
                <><Save size={18} style={{ marginRight: 8 }} /> {editingProduct ? "Salvar" : "Criar"}</>
              )}
            </button>
            {editingProduct && (
              <button type="button" className="cancel-btn" onClick={resetForm}>
                <X size={20} />
              </button>
            )}
          </div>
        </form>
      </S.FormCard>

      <S.FilterBar>
        <div className="search-input">
          <Search size={20} />
          <input 
            type="text" 
            placeholder="Pesquisar produto..." 
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </div>

        <div className="category-filter">
          <Filter size={20} />
          <select value={filterCategory} onChange={(e) => setFilterCategory(e.target.value)}>
            <option value="">Todas as Categorias</option>
            {categories.map(cat => (
              <option key={cat} value={cat}>{cat}</option>
            ))}
          </select>
        </div>
        
        <span className="results-count">
          {filteredProducts.length} itens
        </span>
      </S.FilterBar>

      <S.Table>
        <thead>
          <tr>
            <th>Produto</th>
            <th>Categoria</th>
            <th>Preço</th>
            <th>Ações</th>
          </tr>
        </thead>
        <tbody>
          {filteredProducts.length > 0 ? (
            filteredProducts.map((p) => (
              <tr key={p.id}>
                <td data-label="Produto">
                  <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                    {p.image_url ? (
                      <img src={p.image_url} alt="" style={{ width: '40px', height: '40px', borderRadius: '8px', objectFit: 'cover' }} />
                    ) : (
                      <div style={{ width: '40px', height: '40px', borderRadius: '8px', background: 'var(--border)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                        <Image size={20} color="var(--text-secondary)" />
                      </div>
                    )}
                    <strong>{p.name}</strong>
                  </div>
                </td>
                <td data-label="Categoria">
                  <span className="badge">{p.category}</span>
                </td>
                <td data-label="Preço">
                  <span className="price-text">
                    {Number(p.price).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })}
                  </span>
                </td>
                <td data-label="Ações">
                  <div className="btn-actions">
                    <button onClick={() => handleEdit(p)}><Edit size={18} /></button>
                    <button onClick={() => handleOpenModal(p)}><Trash size={18} /></button>
                  </div>
                </td>
              </tr>
            ))
          ) : (
            <tr>
              <td colSpan="4" style={{ textAlign: 'center', padding: '40px' }}>Nenhum item encontrado.</td>
            </tr>
          )}
        </tbody>
      </S.Table>

      <DeleteModal
        isOpen={modalOpen}
        itemName={selectedProduct?.name}
        onClose={() => setModalOpen(false)}
        onConfirm={handleConfirmDelete}
      />
    </S.AdminContainer>
  );
};

export default ProductAdmin;